import React, { forwardRef, useRef, useState } from 'react'
import clsx from 'clsx'
import { StandardProps } from '../../types/common'
import { CloseIcon } from '../Icons'

import styles from './Menu.module.scss'
import { MenuLink } from './MenuLink'

const WAIT_TIME = 200 // ms
interface SubMenuProps extends StandardProps {
  link?: React.ReactNode
  isOpen: boolean
  isFirstChild?: boolean
  index: number
  handleOpen: (index: number) => void
  handleClose: () => void
}

export const SubMenu = forwardRef<HTMLLIElement, SubMenuProps>(
  (
    {
      children,
      className,
      link,
      isOpen,
      isFirstChild,
      index,
      handleOpen,
      handleClose,
    },
    ref
  ) => {
    const isMenuLink = Boolean(isFirstChild && !children)
    const isMenuDropdown = Boolean(isFirstChild && children)
    const isMenuDropdownItem = Boolean(!isFirstChild)

    const buttonRef = useRef(null)
    const containerRef = useRef<HTMLDivElement>(null)

    const [shouldWait, setShouldWait] = useState(false)

    const handleShouldWait = () => {
      setShouldWait(true)
      setTimeout(() => {
        setShouldWait(false)
      }, WAIT_TIME)
    }

    const handleExpandMenu = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      const element = e.target as HTMLElement
      const sibling = element.nextElementSibling
      if (sibling) {
        const submenu = sibling.querySelector('ul')
        if (submenu) {
          const parentMenuHeight =
            element?.parentElement?.parentElement?.offsetHeight || 0
          const childMenuHeight = submenu.offsetHeight
          if (
            childMenuHeight > parentMenuHeight &&
            element?.parentElement?.parentElement
          ) {
            element.parentElement.parentElement.style.height =
              childMenuHeight + 'px'
          }
        }
      }
    }

    const handleShrinkMenu = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      const element = e.target as HTMLElement
      element?.parentElement?.parentElement?.removeAttribute('style')
    }

    const handleMouseEnter = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (!shouldWait) {
        handleShouldWait()
        if (!isOpen) {
          handleOpen(index)
        }
        if (!isFirstChild) {
          handleExpandMenu(e)
        }
      }
    }

    const handleMouseLeave = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      handleClose()
      if (!isFirstChild) {
        handleShrinkMenu(e)
      }
    }

    const handleClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (!shouldWait) {
        handleShouldWait()
        if (!isFirstChild) {
          handleShrinkMenu(e)
        }
        if (isOpen) {
          handleClose()
        } else {
          handleOpen(index)
          if (!isFirstChild) {
            handleExpandMenu(e)
          }
        }
      }
    }

    return (
      <li
        ref={ref}
        className={clsx(
          {
            [styles['hds-menu__item']]: isMenuDropdown || isMenuLink,
            [styles['has-dropdown']]: isMenuDropdown,
            [styles['hds-menu__dropdown-item']]: isMenuDropdownItem,
            [styles['has-flyout']]: isMenuDropdownItem,
            [styles['open']]: isOpen,
          },
          className
        )}
        onMouseOver={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children ? (
          <button
            ref={buttonRef}
            onClick={handleClick}
            className={clsx(
              isMenuDropdown
                ? styles['hds-menu__link']
                : styles['hds-menu__dropdown-link']
            )}
            aria-haspopup={true}
            aria-expanded={isOpen}
          >
            {isMenuDropdown ? (
              link
            ) : (
              <>
                <span>{link}</span>
                <span className={styles['hds-menu__dropdown-icon']}></span>
              </>
            )}
          </button>
        ) : (
          <MenuLink>{link}</MenuLink>
        )}
        {children && (
          <div
            className={styles['hds-menu__dropdown-wrapper']}
            ref={containerRef}
          >
            {isMenuDropdown && (
              <button
                className={styles['hds-menu__dropdown_close']}
                onClick={handleClose}
              >
                <CloseIcon className={styles.icon} />
              </button>
            )}
            <ul className={styles['hds-menu__dropdown']}>{children}</ul>
          </div>
        )}
      </li>
    )
  }
)
